import "./bubble.scss";

import { faRobot } from "@fortawesome/pro-duotone-svg-icons";
import { faBrush, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import Blockies from "react-18-blockies";
import { ChromePicker } from "react-color";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { format } from "timeago.js";
import useLocalStorageState from "use-local-storage-state";

import { loadExtensions } from "@/extensions";
import { useCeramic, useQueries } from "@/hooks";
import { Page, PromptHistory as PromptHistoryType, PromptTemplate } from "@/types";

import styles from "./Buddies.module.scss";

export const Buddies = () => {
  let { projectId = "global-graph", projectPublicId } = useParams();
  const { address } = useCeramic();
  const queryClient = useQueryClient();
  const { promptTemplates, createPromptTemplate, updatePromptTemplate, currentProject } =
    useQueries();

  if (!currentProject) return <></>;

  const [currentTemplate, setCurrentTemplate] = useState<Partial<PromptTemplate>>();
  const [name, setName] = useState<string>();
  const [colour, setColour] = useState<string>();
  const [prompt, setPrompt] = useState<string>();
  const [gpt4, setGpt4] = useState<boolean>();

  const [buddyId, setBuddyId] = useLocalStorageState<string>(
    `ldgV2BuddyId-${projectPublicId ? projectPublicId : currentProject?.slug}`,
    { defaultValue: "87e503e8-14ba-4e95-b31c-e626d5cb7752" }
  );
  // const [input, setInput] = useLocalStorageState<string>(
  //   `ldgV2Input-${currentProject?.slug}`
  // );
  // const [prompt, setPrompt] = useLocalStorageState<string>(
  //   `ldgV2Prompt-${currentProject?.slug}`
  // );

  const isAuthor = currentTemplate?.user == address;

  useEffect(() => {
    if (promptTemplates.length) setCurrentTemplate(promptTemplates.find((pt) => pt.id == buddyId));
  }, [promptTemplates]);

  useEffect(() => {
    if (currentTemplate) {
      setName(currentTemplate?.name);
      setColour("#" + currentTemplate?.colour.toString(16));
      setPrompt(currentTemplate?.prompt);
      setGpt4(currentTemplate?.model == "gpt-4");
    }
  }, [currentTemplate]);

  const colourPickerChangeComplete = (colour) => {
    setColour(colour.hex);
  };

  return (
    <>
      <Dialog.Close asChild>
        <button className={styles.iconButton} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </Dialog.Close>
      <div className={styles.wrapper}>
        <div className={styles.promptPreview}>
          {currentTemplate?.id ? (
            <div className={styles.promptButtons}>
              <button
                onClick={() => {
                  // setInput(currentTemplate?.human);
                  if (currentTemplate?.id) setBuddyId(currentTemplate?.id);
                }}
              >
                Set as buddy
              </button>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.scroller}>
            <div className={styles.nameWrapper}>
              <div className={styles.buddyWrapper}>
                <FontAwesomeIcon
                  className={styles.buddy}
                  icon={faRobot}
                  size={"6x"}
                  style={{ color: colour }}
                />
                {isAuthor ? (
                  <button id="colourpicker" data-tooltip-place={"right-end"}>
                    <FontAwesomeIcon icon={faBrush} /> Colour
                  </button>
                ) : (
                  <></>
                )}

                <Tooltip anchorSelect="#colourpicker" openOnClick={true} clickable>
                  <ChromePicker color={colour} onChange={colourPickerChangeComplete} />
                </Tooltip>
              </div>
              <div className={styles.name}>
                <div className="bubble grow left">
                  <label htmlFor="name">Hello, my name is</label>
                  <input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isAuthor}
                  />
                </div>
              </div>
            </div>
            {!isAuthor ? (
              <div className={styles.author}>
                Author: <Blockies seed={currentTemplate?.user?.toLowerCase() ?? ""} size={5} />{" "}
                <span>{currentTemplate?.user}</span>
              </div>
            ) : (
              <></>
            )}
            <label htmlFor="prompt" className={styles.promptLabel}>
              Buddy prompt:{" "}
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={!isAuthor}
            ></textarea>
            <label htmlFor="prompt"></label>
            <label>
              <input
                type="checkbox"
                name="history"
                checked={gpt4}
                onChange={(e) => setGpt4(e?.target?.checked)}
                disabled={!isAuthor}
              />
              GPT 4
            </label>
            {isAuthor ? (
              <button
                onClick={async () => {
                  const params = {
                    colour: parseInt((colour ?? "").replace("#", ""), 16),
                    model: gpt4 ? "gpt-4" : "gpt-3.5-turbo-16k-0613",
                    name: name ?? "",
                    prompt: prompt ?? "",
                  };
                  let response;
                  if (!currentTemplate?.id) {
                    response = await createPromptTemplate({
                      project: currentProject?.slug,
                      type: "system",
                      user: address ?? "",
                      ...params,
                    });
                  } else {
                    response = await updatePromptTemplate(currentTemplate?.id, params);
                  }
                  console.log(response);
                  queryClient.invalidateQueries({
                    queryKey: ["prompt-templates", currentProject?.slug],
                  });
                }}
              >
                {!currentTemplate?.id ? "Create" : "Update"}
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className={styles.prompts}>
          <button
            onClick={() => {
              setCurrentTemplate({
                user: address ?? "",
                project: currentProject?.slug,
                model: "gpt-3.5-turbo-16k-0613",
                type: "system",
                prompt: "",
                colour: 0,
                name: "",
              });
            }}
          >
            Create new
          </button>
          {promptTemplates.map((template, i) => (
            <div
              className={classNames(styles.prompt, {
                [styles.active]: template == currentTemplate,
              })}
              key={`${prompt}-prompt-${i}`}
              onClick={() => setCurrentTemplate(template)}
            >
              <div className={styles.title}>
                <FontAwesomeIcon
                  icon={faRobot}
                  style={{ color: "#" + template?.colour?.toString(16) }}
                />
                {template.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
